<template>
    <b-modal
        id="modal-reconcile-logs"
        v-model="showModal"
        title="Reconcile External Backlog Hours"
        size="xl"
        hide-footer
        no-close-on-backdrop
        dialog-class="modal-dialog-elevated"
        data-test="modal-reconcile-logs"
    >
        <b-overlay :show="loading" rounded="lg" opacity="0.6">
            <div class="p-2">
                <!-- Informational Alert -->
                <b-alert v-if="mismatches && mismatches.length > 0" variant="warning" show class="mb-3 shadow-sm border-warning reconcile-alert">
                    <div class="d-flex align-items-center justify-content-between reconcile-alert-header">
                        <div class="d-flex align-items-center min-w-0 mr-2 flex-grow-1" @click="showDetails = !showDetails" style="cursor: pointer;">
                            <span class="h5 mr-2 mb-0 text-warning flex-shrink-0">
                                <i class="bi bi-exclamation-triangle-fill"></i>
                            </span>
                            <strong class="reconcile-alert-title mb-0">External Work Item Log Mismatch Detected</strong>
                        </div>
                        <b-button
                            variant="link"
                            size="sm"
                            class="p-0 text-decoration-none reconcile-info-toggle flex-shrink-0 ml-2"
                            :class="{ 'info-active': showDetails }"
                            :aria-expanded="showDetails ? 'true' : 'false'"
                            aria-label="Toggle explanation details"
                            v-b-tooltip.hover.bottom="showDetails ? 'Hide details' : 'Show details'"
                            data-test="btn-toggle-reconcile-info"
                            @click="showDetails = !showDetails"
                        >
                            <i class="bi" :class="showDetails ? 'bi-info-circle-fill' : 'bi-info-circle'"></i>
                        </b-button>
                    </div>
                    <b-collapse id="collapse-reconcile-info" v-model="showDetails" class="reconcile-alert-collapse">
                        <div class="small reconcile-alert-body mt-2 pt-2 border-top border-warning-subtle">
                            The following work items have completed hours recorded directly in the connected backlog platform outside VelocyTrack.
                            Items exceeding daily capacity are automatically pre-distributed across days with available capacity gaps.
                            Click the <strong>Target Date</strong> on any row to fine-tune dates, adjust hours per day, or toggle Overtime.
                        </div>
                    </b-collapse>
                </b-alert>

                <!-- Start Log Date Notice -->
                <div v-if="startLogDate" class="mb-2 text-muted small">
                    <i class="bi bi-info-circle mr-1 text-primary"></i>
                    Earliest allowed logging date is configured as <strong>{{ startLogDate }}</strong>.
                </div>

                <!-- Reconciliation Grid Shell -->
                <grid-shell
                    class="reconcile-logs-grid"
                    :columns="gridColumnsReconcile"
                    :loading="loading"
                    :empty="!loading && (!mismatches || !mismatches.length)"
                    empty-title="All Backlog Hours in Sync!"
                    empty-message="No discrepancies found between external backlog work items and your local VelocyTrack time logs."
                    empty-icon="bi bi-check-circle-fill"
                >
                    <template #view-actions>
                        <b-button
                            size="sm"
                            variant="outline-secondary"
                            class="px-2 py-1 font-weight-bold"
                            :disabled="loading || isSyncingAny"
                            @click="refreshMismatches"
                            title="Refresh external backlog hours from Azure DevOps"
                            aria-label="Refresh external backlog hours"
                            data-test="btn-refresh-reconcile"
                        >
                            <i class="bi bi-arrow-clockwise mr-1"></i>Refresh
                        </b-button>
                    </template>
                    <div
                        v-for="item in mismatches"
                        :key="item.workItemId"
                        class="reconcile-row-shell"
                        :class="{ 'row-synced': item.synced }"
                        data-test="row-reconcile-item"
                        :data-test-id="item.workItemId"
                    >
                        <b-row align-h="start" align-v="center" class="grid-row">
                            <!-- Work Item Nav Link Button -->
                            <b-col class="grid-col-workitem">
                                <nav-link-button
                                    :id="item.workItemId"
                                    :href="item.url"
                                    :type-icon="item.workItemTypeIcon"
                                    :work-item-type="item.workItemType"
                                    title="Open work item in Azure DevOps"
                                ></nav-link-button>
                            </b-col>

                            <!-- Title -->
                            <b-col class="grid-col-title">
                                <div class="font-weight-bold text-truncate title-text" :title="item.title">
                                    {{ item.title }}
                                </div>
                                <div v-if="item.revisedBy" class="extra-small text-muted text-truncate">
                                    <i class="bi bi-person mr-1"></i>Updated by: {{ item.revisedBy }}
                                </div>
                            </b-col>

                            <!-- Platform vs Local -->
                            <b-col class="grid-col-platform text-center">
                                <div class="small platform-hours-wrap">
                                    <span class="text-muted mr-1 d-lg-none">Logged:</span>
                                    <span class="text-info font-weight-bold" title="Hours in external backlog">{{ item.completedWork }}h</span>
                                    <span class="text-muted mx-1">/</span>
                                    <span class="text-secondary" title="Hours currently logged in VelocyTrack">{{ item.localLoggedHours }}h</span>
                                    <span class="text-muted ml-1 d-lg-none extra-small">(backlog / local)</span>
                                </div>
                                <div class="extra-small text-muted d-none d-lg-block">backlog / local</div>
                            </b-col>

                            <!-- Delta -->
                            <b-col class="grid-col-delta text-center">
                                <b-badge variant="warning" class="font-weight-bold px-2 py-1 delta-badge">
                                    +{{ item.deltaHours }}h
                                </b-badge>
                            </b-col>

                            <!-- Target Date & Capacity Allocation Input -->
                            <b-col class="grid-col-targetdate">
                                <span class="mobile-col-caption d-lg-none">Target Date & Allocation:</span>
                                <picker-capacity-date
                                    :ref="'picker-' + item.workItemId"
                                    :value="item.selectedDate"
                                    @input="onSelectedDateChange(item, $event)"
                                    :allocations="item.allocations"
                                    @update:allocations="onAllocationsChange(item, $event)"
                                    @change-allocations="onAllocationsChange(item, $event)"
                                    :multi="isItemMulti(item)"
                                    :delta-hours="item.deltaHours"
                                    :daily-capacity="dailyCapacity"
                                    :calendar-day-statuses="getEffectiveDayStatusesForItem(item)"
                                    :public-holidays="publicHolidays"
                                    :suggested-date="item.suggestedDate ? item.suggestedDate.split('T')[0] : todayYMD"
                                    :min="startLogDate"
                                    :max="todayYMD"
                                    :disabled="item.syncing || item.synced"
                                ></picker-capacity-date>
                            </b-col>

                            <!-- Work Type Select -->
                            <b-col class="grid-col-worktype">
                                <div class="worktype-wrapper d-flex align-items-center w-100">
                                    <span class="mobile-col-caption d-lg-none mr-2 mb-0">Work Type:</span>
                                    <b-form-select
                                        size="sm"
                                        v-model="item.workType"
                                        :options="workTypeOptions"
                                        :disabled="item.syncing || item.synced"
                                        class="reconcile-worktype-select"
                                    ></b-form-select>
                                </div>
                            </b-col>

                            <!-- Sync Action -->
                            <b-col class="grid-col-action text-center">
                                <span v-if="item.synced" class="text-success font-weight-bold small d-inline-flex align-items-center">
                                    <i class="bi bi-check-circle-fill mr-1"></i>Synced
                                </span>
                                <div v-else class="d-inline-flex align-items-center justify-content-center flex-nowrap reconcile-action-group">
                                    <b-button
                                        size="sm"
                                        variant="outline-info"
                                        class="px-2 mr-1 font-weight-bold shadow-sm btn-reconcile-edit"
                                        :disabled="item.syncing"
                                        @click="openItemAllocationEditor(item)"
                                        data-test="btn-edit-reconcile-item"
                                        aria-label="Edit target dates and hours allocation"
                                        title="Edit target dates and hours allocation"
                                    >
                                        <i class="bi bi-pencil-square mr-1"></i>
                                        <span>Edit</span>
                                    </b-button>
                                    <b-button
                                        size="sm"
                                        variant="success"
                                        class="px-2 font-weight-bold shadow-sm btn-reconcile-sync"
                                        :disabled="item.syncing || !isItemReadyToSync(item)"
                                        @click="syncItem(item)"
                                        data-test="btn-sync-reconcile-item"
                                        :title="!isItemReadyToSync(item) ? 'Allocate all hours across dates before syncing' : 'Sync hours with VelocyTrack and Azure DevOps'"
                                    >
                                        <b-spinner v-if="item.syncing" small class="mr-1"></b-spinner>
                                        <i v-else class="bi bi-arrow-repeat mr-1"></i>
                                        <span>{{ getItemSyncLabel(item) }}</span>
                                    </b-button>
                                </div>
                            </b-col>
                        </b-row>
                    </div>
                </grid-shell>

                <!-- Footer Actions -->
                <div class="d-flex flex-wrap justify-content-between align-items-center pt-2 border-top gap-2 mt-2">
                    <div>
                        <span v-if="unsyncedCount > 0" class="small text-muted">
                            {{ unsyncedCount }} item{{ unsyncedCount > 1 ? 's' : '' }} pending reconciliation ({{ totalUnsyncedHours }}h total).
                        </span>
                    </div>
                    <div class="d-flex align-items-center">
                        <b-button
                            variant="outline-secondary"
                            size="sm"
                            class="mr-2 px-3"
                            @click="close"
                            data-test="btn-close-reconcile"
                        >
                            {{ unsyncedCount === 0 ? 'Done' : 'Close' }}
                        </b-button>
                        <b-button
                            v-if="unsyncedCount > 1"
                            variant="primary"
                            size="sm"
                            class="px-3 font-weight-bold shadow-sm"
                            :disabled="isSyncingAny || !canSyncAll"
                            @click="syncAll"
                            data-test="btn-sync-all-reconcile"
                        >
                            <b-spinner v-if="isSyncingAny" small class="mr-1"></b-spinner>
                            <i v-else class="bi bi-check-all mr-1"></i>Sync All ({{ unsyncedCount }})
                        </b-button>
                    </div>
                </div>
            </div>
            <template #overlay>
                <load-spinner text="Checking external log entries..." />
            </template>
        </b-overlay>
    </b-modal>
</template>

<script>
import NavLinkButton from './nav-link-button.vue';
import PickerCapacityDate from './picker-capacity-date.vue';
import GridShell from './grid-shell.vue';
import toastMixin from '../mixinToast';
import { autoFillGaps, getDayExistingHours, formatLocalDate, parseLocalDate } from '../reconcileAllocation';

export default {
    name: 'modal-reconcile-logs',
    components: {
        'grid-shell': GridShell,
        'nav-link-button': NavLinkButton,
        'picker-capacity-date': PickerCapacityDate
    },
    mixins: [toastMixin],
    props: {
        startLogDate: {
            type: String,
            default: null
        },
        workTypes: {
            type: Array,
            default: () => ['Development', 'Review', 'Analysis', 'Testing', 'Bugfix', 'Documentation']
        },
        dailyCapacity: {
            type: Number,
            default: 8
        },
        calendarDayStatuses: {
            type: Array,
            default: () => []
        },
        publicHolidays: {
            type: [Array, Object, Function],
            default: () => []
        }
    },
    data() {
        return {
            showModal: false,
            loading: false,
            showDetails: false,
            mismatches: [],
            rangeDayStatuses: [],
            lastSprintPath: null,
            gridColumnsReconcile: [
                { key: "workitem", label: "Work Item", width: "130px" },
                { key: "title", label: "Title", width: "minmax(13rem, 1fr)", headerClass: "text-left pl-1" },
                { key: "platform", label: "Platform / Local", width: "125px", headerClass: "text-center" },
                { key: "delta", label: "Delta", width: "80px", headerClass: "text-center" },
                { key: "targetdate", label: "Target Date & Allocation", width: "260px" },
                { key: "worktype", label: "Work Type", width: "135px" },
                { key: "action", label: "Action", width: "165px", headerClass: "text-center" }
            ]
        };
    },
    computed: {
        todayYMD() {
            return formatLocalDate(new Date());
        },
        baseDayStatuses() {
            const mergedMap = new Map();
            (this.rangeDayStatuses || []).forEach(s => {
                if (!s || !s.date) return;
                const ymd = String(s.date).split('T')[0];
                mergedMap.set(ymd, this.buildDayStatusEntry(ymd, s, 0));
            });
            (this.calendarDayStatuses || []).forEach(s => {
                if (!s || !s.date) return;
                const ymd = String(s.date).split('T')[0];
                mergedMap.set(ymd, this.buildDayStatusEntry(ymd, s, 0));
            });
            return Array.from(mergedMap.values());
        },
        workTypeOptions() {
            const list = (this.workTypes && this.workTypes.length)
                ? this.workTypes
                : ['Development', 'Review', 'Analysis', 'Testing', 'Bugfix', 'Documentation'];
            return list.map(wt => ({ value: wt, text: wt }));
        },
        unsyncedCount() {
            return this.mismatches.filter(m => !m.synced).length;
        },
        totalUnsyncedHours() {
            const sum = this.mismatches
                .filter(m => !m.synced)
                .reduce((acc, m) => acc + (parseFloat(m.deltaHours) || 0), 0);
            return Math.round(sum * 100) / 100;
        },
        isSyncingAny() {
            return this.mismatches.some(m => m.syncing);
        },
        canSyncAll() {
            const pending = this.mismatches.filter(m => !m.synced);
            return pending.length > 0 && pending.every(item => this.isItemReadyToSync(item));
        }
    },
    methods: {
        isItemMulti(item) {
            if (!item) return false;
            return (parseFloat(item.deltaHours) || 0) > this.dailyCapacity || (item.allocations && item.allocations.length > 1);
        },
        buildDayStatusEntry(ymd, existing, addedHours = 0) {
            const isAbs = Boolean(existing && (existing.style === 'absence' || existing.status === 'absence' || existing.isAbsence));
            const baseHours = existing ? getDayExistingHours(ymd, [existing], this.dailyCapacity) : 0;
            const safeBase = baseHours === -1 ? 0 : baseHours;
            const newTotal = Math.round((safeBase + addedHours) * 100) / 100;
            const style = isAbs
                ? 'absence'
                : (newTotal > this.dailyCapacity
                    ? 'overflaw'
                    : (newTotal === this.dailyCapacity ? 'done' : (newTotal > 0 ? 'pending' : '')));
            const status = isAbs
                ? 'absence'
                : (newTotal > this.dailyCapacity
                    ? 'overflaw'
                    : (newTotal === this.dailyCapacity ? 'completed' : (newTotal > 0 ? 'incomplete' : '')));
            return {
                ...(existing || {}),
                date: ymd,
                isAbsence: isAbs,
                hours: newTotal,
                totalHours: newTotal,
                style,
                status
            };
        },
        async ensureRangeDayStatusesAsync() {
            if (!this.$http || typeof this.$http.get !== 'function') return false;
            const end = this.todayYMD;
            let start = this.startLogDate ? String(this.startLogDate).split('T')[0] : null;
            if (!start) {
                const d = parseLocalDate(end) || new Date();
                d.setDate(d.getDate() - 60);
                start = formatLocalDate(d);
            }
            try {
                const resp = await this.$http.get('/TimeManagement/GetPickedRangeDayStatuses', {
                    params: { start, end }
                });
                if (resp?.data?.calendarDayStatuses && Array.isArray(resp.data.calendarDayStatuses)) {
                    this.rangeDayStatuses = resp.data.calendarDayStatuses;
                    return true;
                }
            } catch (e) {
                // Non-blocking fallback to prop calendarDayStatuses
            }
            return false;
        },
        show(mismatchesList = null, sprintPath = null) {
            return this.open(mismatchesList, sprintPath);
        },
        refreshMismatches() {
            return this.open(null, this.lastSprintPath);
        },
        openItemAllocationEditor(item) {
            if (!item || item.syncing || item.synced) return;
            const refKey = `picker-${item.workItemId}`;
            const rawRef = this.$refs[refKey];
            const picker = Array.isArray(rawRef) ? rawRef[0] : rawRef;
            if (picker && typeof picker.openPopover === 'function') {
                picker.openPopover();
            } else if (picker && typeof picker.togglePopover === 'function') {
                picker.togglePopover();
            }
        },
        async open(mismatchesList = null, sprintPath = null) {
            this.showModal = true;
            if (sprintPath !== undefined) {
                this.lastSprintPath = sprintPath;
            }
            this.showDetails = typeof window !== 'undefined' ? window.innerWidth >= 992 : false;
            if (mismatchesList && Array.isArray(mismatchesList)) {
                this.initMismatches(mismatchesList);
                const fetched = await this.ensureRangeDayStatusesAsync();
                if (fetched && this.mismatches.every(m => !m.synced && !m.syncing && !m._userEdited)) {
                    this.initMismatches(mismatchesList);
                }
                return;
            }

            this.loading = true;
            this.mismatches = [];
            try {
                const params = {};
                if (sprintPath) {
                    params.sprint = sprintPath;
                }
                const [resp] = await Promise.all([
                    this.$http.get('/TimeManagement/DetectWorkItemMismatches', { params }),
                    this.ensureRangeDayStatusesAsync()
                ]);
                const fetchedList = resp.data || [];
                this.initMismatches(fetchedList);
                this.$emit('mismatches-loaded', fetchedList);
            } catch (err) {
                console.error('Failed to detect work item mismatches', err);
                this.toastError('Detection Failed', 'Could not detect work item mismatches.');
                this.mismatches = [];
            } finally {
                this.loading = false;
            }
        },
        initMismatches(list) {
            const workingStatusMap = new Map();
            (this.baseDayStatuses || []).forEach(s => {
                if (!s || !s.date) return;
                const ymd = String(s.date).split('T')[0];
                workingStatusMap.set(ymd, this.buildDayStatusEntry(ymd, s, 0));
            });

            this.mismatches = list.map(item => {
                let suggested = item.suggestedDate ? item.suggestedDate.split('T')[0] : this.todayYMD;
                if (this.startLogDate && suggested < this.startLogDate) {
                    suggested = this.startLogDate;
                }
                if (suggested > this.todayYMD) {
                    suggested = this.todayYMD;
                }

                const delta = Math.round((parseFloat(item.deltaHours) || 0) * 100) / 100;
                let allocations = [];
                if (delta > 0) {
                    const autoFillRes = autoFillGaps({
                        deltaHours: delta,
                        dailyCapacity: this.dailyCapacity,
                        calendarDayStatuses: Array.from(workingStatusMap.values()),
                        publicHolidays: this.publicHolidays,
                        minDate: this.startLogDate,
                        maxDate: this.todayYMD,
                        suggestedDate: suggested,
                        allowOvertime: false
                    });
                    allocations = (autoFillRes.allocations && autoFillRes.allocations.length > 0)
                        ? autoFillRes.allocations
                        : [{ date: suggested, hours: delta }];
                } else {
                    allocations = [{ date: suggested, hours: delta }];
                }

                const selectedDate = (allocations.length >= 1 && allocations[0].date)
                    ? allocations[0].date
                    : suggested;

                allocations.forEach(alloc => {
                    if (!alloc || !alloc.date) return;
                    const ymd = String(alloc.date).split('T')[0];
                    const hrs = parseFloat(alloc.hours || 0);
                    if (hrs <= 0) return;
                    const existing = workingStatusMap.get(ymd);
                    workingStatusMap.set(ymd, this.buildDayStatusEntry(ymd, existing, hrs));
                });

                return {
                    ...item,
                    selectedDate,
                    allocations,
                    workType: item.workType || 'Development',
                    syncing: false,
                    synced: false,
                    _userEdited: false
                };
            });
        },
        onSelectedDateChange(item, newVal) {
            if (!item) return;
            item._userEdited = true;
            if (typeof newVal === 'string') {
                item.selectedDate = newVal;
                if (!this.isItemMulti(item)) {
                    const delta = Math.round((parseFloat(item.deltaHours) || 0) * 100) / 100;
                    item.allocations = newVal ? [{ date: newVal, hours: delta }] : [];
                }
            } else if (Array.isArray(newVal)) {
                this.onAllocationsChange(item, newVal);
            }
        },
        onAllocationsChange(item, newAllocations) {
            if (!item) return;
            item._userEdited = true;
            const list = Array.isArray(newAllocations) ? newAllocations : [];
            item.allocations = list;
            if (list.length > 0 && list[0].date) {
                item.selectedDate = list[0].date;
            }
        },
        getEffectiveDayStatusesForItem(targetItem) {
            const statusMap = new Map();
            (this.baseDayStatuses || []).forEach(s => {
                if (!s || !s.date) return;
                const ymd = String(s.date).split('T')[0];
                statusMap.set(ymd, this.buildDayStatusEntry(ymd, s, 0));
            });

            (this.mismatches || []).forEach(m => {
                if (!m || m === targetItem) return;
                const allocs = (m.allocations && m.allocations.length > 0)
                    ? m.allocations
                    : (m.selectedDate ? [{ date: m.selectedDate, hours: m.deltaHours }] : []);
                allocs.forEach(alloc => {
                    if (!alloc || !alloc.date) return;
                    const ymd = String(alloc.date).split('T')[0];
                    const hrs = parseFloat(alloc.hours || 0);
                    if (hrs <= 0) return;
                    const existing = statusMap.get(ymd);
                    statusMap.set(ymd, this.buildDayStatusEntry(ymd, existing, hrs));
                });
            });

            return Array.from(statusMap.values());
        },
        isItemReadyToSync(item) {
            if (!item.allocations || item.allocations.length === 0) {
                return !!item.selectedDate;
            }
            const sum = item.allocations.reduce((acc, a) => acc + (parseFloat(a.hours) || 0), 0);
            const delta = parseFloat(item.deltaHours) || 0;
            return Math.abs(sum - delta) <= 0.01;
        },
        getItemSyncLabel(item) {
            if (item.allocations && item.allocations.length > 1) {
                return `Sync (${item.allocations.length})`;
            }
            return 'Sync';
        },
        close() {
            this.showModal = false;
        },
        async syncItem(item) {
            const isMulti = item.allocations && item.allocations.length > 0;
            if (!isMulti && !item.selectedDate) {
                this.toastWarning('Date Required', 'Please pick a valid target date for reconciliation.');
                return;
            }

            if (isMulti) {
                const allocatedSum = Math.round(item.allocations.reduce((sum, a) => sum + (parseFloat(a.hours) || 0), 0) * 100) / 100;
                const delta = Math.round((parseFloat(item.deltaHours) || 0) * 100) / 100;
                if (allocatedSum < delta) {
                    this.toastWarning('Incomplete Allocation', `Please allocate all ${delta}h across dates or enable Overtime in the picker (${allocatedSum}h / ${delta}h allocated).`);
                    return;
                }
            }

            item.syncing = true;
            try {
                const allocsPayload = isMulti
                    ? item.allocations.map(a => ({ date: a.date, hours: parseFloat(a.hours) }))
                    : [{ date: item.selectedDate, hours: parseFloat(item.deltaHours) }];

                const payload = {
                    workItemId: item.workItemId,
                    workType: item.workType,
                    comment: `Reconciled +${item.deltaHours}h from external backlog (${item.workItemType})`,
                    allocations: allocsPayload
                };

                const resp = await this.$http.post('/TimeManagement/ReconcileWorkItemMultiLog', payload);
                item.synced = true;
                const datesCount = allocsPayload.length;
                this.toastSuccess('Reconciled!', `Recorded +${item.deltaHours}h across ${datesCount} date${datesCount > 1 ? 's' : ''} for #${item.workItemId}.`);
                this.$emit('reconciled', {
                    item,
                    timeLog: resp.data?.createdLogs?.[0],
                    createdLogs: resp.data?.createdLogs || []
                });
            } catch (err) {
                console.error('Failed to reconcile work item log', err);
                const errMsg = err?.response?.data?.message || err?.response?.data || err?.message || 'Failed to sync log entry.';
                this.toastError('Sync Failed', errMsg);
            } finally {
                item.syncing = false;
            }
        },
        async syncAll() {
            const pending = this.mismatches.filter(m => !m.synced && !m.syncing && this.isItemReadyToSync(m));
            for (const item of pending) {
                await this.syncItem(item);
            }
        }
    }
};
</script>

<style scoped>
    @media (min-width: 768px) {
        ::v-deep .modal-dialog-elevated {
            max-width: min(1240px, 94vw) !important;
            width: 94vw !important;
            margin: 1.5rem auto !important;
        }
    }
    @media (min-width: 1400px) {
        ::v-deep .modal-dialog-elevated {
            max-width: 1280px !important;
        }
    }

    .reconcile-logs-grid {
        padding-left: 0 !important;
        padding-right: 0 !important;
        width: 100% !important;
    }
    .reconcile-logs-grid ::v-deep .grid-shell {
        width: 100% !important;
        padding-left: 0;
        padding-right: 0;
    }
    .reconcile-logs-grid ::v-deep .grid-shell-scroll {
        width: 100% !important;
        min-height: 240px;
        max-height: calc(100vh - 280px);
        overflow-x: auto;
    }

    /* Desktop Row Cards */
    .reconcile-row-shell {
        background-color: var(--vt-surface-card, #ffffff);
        border: 1px solid var(--vt-border, #e2e8f0);
        border-radius: 0.5rem;
        margin-bottom: 0.4rem;
        transition: background-color 0.15s ease, border-color 0.15s ease;
    }

    [data-theme="dark"] .reconcile-row-shell {
        background-color: rgba(30, 41, 59, 0.4);
        border-color: rgba(255, 255, 255, 0.08);
    }

    .reconcile-row-shell.row-synced {
        background-color: rgba(40, 167, 69, 0.08) !important;
        border-color: rgba(40, 167, 69, 0.35) !important;
    }

    [data-theme="dark"] .reconcile-row-shell.row-synced {
        background-color: rgba(40, 167, 69, 0.15) !important;
        border-color: rgba(40, 167, 69, 0.4) !important;
    }

    .reconcile-row-shell .grid-row {
        padding: 0.45rem 0.65rem;
        align-items: center;
    }

    .title-text {
        font-size: 0.92rem;
        line-height: 1.35;
        color: var(--vt-text, #1e293b);
    }

    [data-theme="dark"] .title-text {
        color: #f8fafc;
    }

    .delta-badge {
        font-size: 0.85rem;
    }

    .btn-reconcile-sync {
        min-width: 68px;
        background-color: #28a745 !important;
        border-color: #28a745 !important;
        color: #ffffff !important;
    }

    .btn-reconcile-sync:hover:not(:disabled) {
        background-color: #218838 !important;
        border-color: #1e7e34 !important;
        color: #ffffff !important;
    }

    .btn-reconcile-sync:disabled {
        background-color: rgba(40, 167, 69, 0.5) !important;
        border-color: rgba(40, 167, 69, 0.4) !important;
        color: rgba(255, 255, 255, 0.7) !important;
    }

    .mobile-col-caption {
        display: block;
        font-size: 0.74rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: var(--vt-muted, #94a3b8);
        margin-bottom: 0.2rem;
    }

    .reconcile-alert {
        border-radius: 0.55rem;
    }

    .reconcile-alert-title {
        font-size: 0.95rem;
        font-weight: 700;
        line-height: 1.3;
    }

    .reconcile-info-toggle {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 0.35rem;
        font-size: 1.15rem;
        color: #ffc107 !important;
        transition: background-color 0.15s ease, color 0.15s ease;
    }

    .reconcile-info-toggle:hover,
    .reconcile-info-toggle:focus {
        background-color: rgba(255, 193, 7, 0.18);
        color: #ffc107 !important;
    }

    [data-theme="light"] .reconcile-info-toggle {
        color: #856404 !important;
    }

    [data-theme="light"] .reconcile-info-toggle:hover,
    [data-theme="light"] .reconcile-info-toggle:focus {
        background-color: rgba(133, 100, 4, 0.12);
        color: #856404 !important;
    }

    .border-warning-subtle {
        border-color: rgba(255, 193, 7, 0.3) !important;
    }

    [data-theme="light"] .border-warning-subtle {
        border-color: rgba(133, 100, 4, 0.25) !important;
    }

    @media (max-width: 991.98px) {
        .reconcile-alert-title {
            font-size: 0.88rem;
        }

        .reconcile-alert-body {
            font-size: 0.82rem;
            line-height: 1.4;
        }
    }
</style>

<style>
    /* Mobile Card Layout for Reconcile Logs Grid (<= 991.98px) */
    @media (max-width: 991.98px) {
        .reconcile-logs-grid .reconcile-row-shell {
            margin-bottom: 0.75rem !important;
        }

        .reconcile-logs-grid .reconcile-row-shell .grid-row {
            display: flex !important;
            flex-wrap: wrap !important;
            align-items: center !important;
            gap: 0.4rem 0.5rem !important;
            padding: 0.75rem 0.85rem !important;
            background-color: var(--vt-surface-card, rgba(30, 41, 59, 0.45)) !important;
            border: 1px solid var(--vt-border, rgba(148, 163, 184, 0.22)) !important;
            border-radius: 0.65rem !important;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12) !important;
            height: auto !important;
        }

        [data-theme="dark"] .reconcile-logs-grid .reconcile-row-shell .grid-row {
            background-color: rgba(30, 41, 59, 0.65) !important;
            border-color: rgba(148, 163, 184, 0.24) !important;
        }

        [data-theme="light"] .reconcile-logs-grid .reconcile-row-shell .grid-row {
            background-color: #ffffff !important;
            border-color: rgba(203, 213, 225, 0.85) !important;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
        }

        .reconcile-logs-grid .reconcile-row-shell.row-synced .grid-row {
            border-color: rgba(40, 167, 69, 0.45) !important;
            background-color: rgba(40, 167, 69, 0.08) !important;
        }

        [data-theme="dark"] .reconcile-logs-grid .reconcile-row-shell.row-synced .grid-row {
            background-color: rgba(40, 167, 69, 0.15) !important;
            border-color: rgba(40, 167, 69, 0.4) !important;
        }

        /* Order 1: Top row elements */
        .reconcile-logs-grid .grid-row > .grid-col-workitem {
            order: 1 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            padding: 0 !important;
        }

        .reconcile-logs-grid .grid-row > .grid-col-delta {
            order: 2 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            padding: 0 !important;
            margin-left: 0.25rem !important;
        }

        .reconcile-logs-grid .grid-row > .grid-col-action {
            order: 3 !important;
            flex: 0 0 auto !important;
            width: auto !important;
            max-width: none !important;
            padding: 0 !important;
            margin-left: auto !important;
        }

        .reconcile-logs-grid .grid-row > .grid-col-action .btn-reconcile-sync,
        .reconcile-logs-grid .grid-row > .grid-col-action .btn-reconcile-edit {
            height: 29px;
            font-size: 0.78rem !important;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 0.4rem;
        }
        .reconcile-logs-grid .grid-row > .grid-col-action .btn-reconcile-sync {
            min-width: 68px;
        }

        /* Order 2: Full-width Title */
        .reconcile-logs-grid .grid-row > .grid-col-title {
            order: 4 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin-top: 0.15rem !important;
            margin-bottom: 0.2rem !important;
            text-align: left !important;
        }

        .reconcile-logs-grid .grid-col-title .title-text {
            font-size: 0.95rem !important;
            font-weight: 600 !important;
            line-height: 1.35 !important;
        }

        /* Order 3: Platform / Local */
        .reconcile-logs-grid .grid-row > .grid-col-platform {
            order: 5 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin-bottom: 0.25rem !important;
            text-align: left !important;
        }

        /* Order 4: Target Date & Allocation */
        .reconcile-logs-grid .grid-row > .grid-col-targetdate {
            order: 6 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin-bottom: 0.35rem !important;
        }

        /* Order 5: Work Type Select */
        .reconcile-logs-grid .grid-row > .grid-col-worktype {
            order: 7 !important;
            flex: 0 0 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
        }

        .reconcile-logs-grid .reconcile-worktype-select {
            width: 100%;
            height: 31px !important;
            font-size: 0.85rem !important;
        }
    }

    #modal-reconcile-logs .btn-reconcile-sync {
        background-color: #28a745 !important;
        border-color: #28a745 !important;
        color: #ffffff !important;
    }

    #modal-reconcile-logs .btn-reconcile-sync:hover:not(:disabled) {
        background-color: #218838 !important;
        border-color: #1e7e34 !important;
        color: #ffffff !important;
    }

    #modal-reconcile-logs .btn-reconcile-sync:disabled {
        background-color: rgba(40, 167, 69, 0.5) !important;
        border-color: rgba(40, 167, 69, 0.4) !important;
        color: rgba(255, 255, 255, 0.7) !important;
    }

    #modal-reconcile-logs [data-test="btn-sync-all-reconcile"] {
        background-color: #007bff !important;
        border-color: #007bff !important;
        color: #ffffff !important;
    }

    #modal-reconcile-logs [data-test="btn-sync-all-reconcile"]:hover:not(:disabled) {
        background-color: #0069d9 !important;
        border-color: #0062cc !important;
        color: #ffffff !important;
    }

    #modal-reconcile-logs [data-test="btn-sync-all-reconcile"]:disabled {
        background-color: rgba(0, 123, 255, 0.5) !important;
        border-color: rgba(0, 123, 255, 0.4) !important;
        color: rgba(255, 255, 255, 0.7) !important;
    }

    #modal-reconcile-logs [data-test="btn-close-reconcile"] {
        color: var(--vt-text, #495057) !important;
        border-color: var(--vt-border, #6c757d) !important;
        background-color: transparent !important;
    }

    #modal-reconcile-logs [data-test="btn-close-reconcile"]:hover {
        color: #ffffff !important;
        background-color: #6c757d !important;
        border-color: #6c757d !important;
    }
</style>
